/**
 * PROJECT FINISHES (spec §82).
 *
 * "LVP throughout, carpet in the bedrooms, paint every wall and ceiling"
 * is three decisions, not sixty. This engine turns those decisions plus
 * the design's own geometry into the coverage each material actually has.
 *
 * THE GOVERNING PRINCIPLE: DEFAULTS ARE ALLOWED, LOCKED-IN ASSUMPTIONS
 * ARE NOT. Berrier Reno may suggest carpet for a bedroom; it may never
 * make that suggestion indistinguishable from something the owner said,
 * and it may never spend money on the strength of it.
 *
 * PRECEDENCE, deterministic and in this order (spec §82-12):
 *
 *   explicit room override  >  project finish  >  room-type suggestion  >  nothing
 *
 * COVERAGE IS A PARTITION. Every eligible square foot belongs to exactly
 * one material, so the materials always sum back to the eligible area —
 * an override does not add a second layer of flooring to a room, it
 * moves that room's area from one material to another.
 *
 * Pure: no I/O, no storage, no clock. The service applies the result.
 */
import { grossWallAreaX100 } from "../domain/room";

export type FinishSurface = "floor" | "walls" | "ceiling";

export interface FinishMaterial {
  key: string;
  displayName: string;
  surface: FinishSurface;
  /** The assembly that prices it — never a second cost path (§82-30). */
  templateId: string;
  /** The option that asks whether the old covering is being taken up. */
  removalOptionId: string | null;
}

/**
 * What a finish can be. Every entry names a seeded assembly: choosing a
 * finish is choosing scope the ordinary estimating engine already prices.
 */
export const FINISH_MATERIALS: readonly FinishMaterial[] = [
  { key: "lvp", displayName: "Luxury Vinyl Plank", surface: "floor", templateId: "asm-install-lvp", removalOptionId: "asm-install-lvp#opt-remove" },
  { key: "carpet", displayName: "Carpet", surface: "floor", templateId: "asm-install-carpet", removalOptionId: "asm-install-carpet#opt-remove" },
  { key: "tile", displayName: "Tile Flooring", surface: "floor", templateId: "asm-tile-floor", removalOptionId: "asm-tile-floor#opt-remove" },
  { key: "wall-paint", displayName: "Wall Paint", surface: "walls", templateId: "asm-paint-interior", removalOptionId: null },
  { key: "ceiling-paint", displayName: "Ceiling Paint", surface: "ceiling", templateId: "asm-paint-interior", removalOptionId: null },
];

export const materialByKey = (key: string): FinishMaterial | null =>
  FINISH_MATERIALS.find((m) => m.key === key) ?? null;

export const materialsFor = (surface: FinishSurface): FinishMaterial[] =>
  FINISH_MATERIALS.filter((m) => m.surface === surface);

/* ------------------------------------------------------------------ *
 * Eligibility (spec §82-14)
 * ------------------------------------------------------------------ */

/**
 * "Entire project" means every FINISHABLE room. A porch is not carpeted
 * and a garage is a decision, not an assumption — so ambiguous spaces are
 * left out of the default and can be added one room at a time.
 */
const NEVER_ELIGIBLE = new Set(["exterior-area"]);
const NOT_BY_DEFAULT = new Set(["garage", "utility-room"]);

export function eligibility(roomTypeId: string): "eligible" | "by-request" | "never" {
  if (NEVER_ELIGIBLE.has(roomTypeId)) return "never";
  if (NOT_BY_DEFAULT.has(roomTypeId)) return "by-request";
  return "eligible";
}

/* ------------------------------------------------------------------ *
 * Suggestions (spec §82-1/3)
 * ------------------------------------------------------------------ */

/** What a room of this type usually gets. A SUGGESTION, always labelled. */
const ROOM_TYPE_FLOOR: Readonly<Record<string, string>> = {
  bedroom: "carpet",
  bathroom: "tile",
  kitchen: "lvp",
  "laundry-room": "lvp",
  "living-room": "lvp",
  "dining-room": "lvp",
  hallway: "lvp",
  entry: "lvp",
  office: "lvp",
  closet: "carpet",
};

export function suggestedMaterial(roomTypeId: string, surface: FinishSurface): string | null {
  if (surface === "floor") return ROOM_TYPE_FLOOR[roomTypeId] ?? null;
  // Paint is not suggested by room type: painting everything is a project
  // decision, and guessing it room by room is exactly the noise §82 removes.
  return null;
}

/* ------------------------------------------------------------------ *
 * Resolution
 * ------------------------------------------------------------------ */

export interface FinishRoom {
  roomId: string;
  roomName: string;
  roomTypeId: string;
  /** Authoritative geometry, x100. Null when the room is not measured. */
  floorAreaX100: number | null;
  perimeterX100: number | null;
  /** Effective wall height in inches (room, else floor default, else app). */
  wallHeightInches: number;
}

export interface FinishProjectChoice {
  surface: FinishSurface;
  materialKey: string;
  /** False switches the whole-project finish off without forgetting it. */
  enabled: boolean;
}

export interface FinishRoomOverride {
  roomId: string;
  surface: FinishSurface;
  /** Null with excluded = true means "no finish in this room". */
  materialKey: string | null;
  excluded: boolean;
}

export type FinishSource = "room-override" | "project-default" | "room-type-suggestion" | "none";

export interface ResolvedRoomFinish {
  roomId: string;
  roomName: string;
  surface: FinishSurface;
  materialKey: string | null;
  source: FinishSource;
  /** The surface's own area, x100 — floor, wall or ceiling (§82-10/15/18). */
  areaX100: number | null;
  eligibility: "eligible" | "by-request" | "never";
}

export interface FinishCoverage {
  surface: FinishSurface;
  materialKey: string;
  displayName: string;
  areaX100: number;
  roomIds: string[];
  /** Rooms counted, and rooms whose geometry nobody has measured yet. */
  measured: number;
  unmeasured: number;
}

export interface FinishReconciliation {
  surface: FinishSurface;
  /** Every eligible square foot in play, before any override. */
  eligibleAreaX100: number;
  /** What the project-wide choice covers once overrides are taken out. */
  defaultAreaX100: number;
  overrides: Array<{ roomId: string; roomName: string; materialKey: string | null; areaX100: number }>;
  excludedAreaX100: number;
}

export interface FinishResolution {
  rooms: ResolvedRoomFinish[];
  coverage: FinishCoverage[];
  reconciliation: FinishReconciliation[];
}

/**
 * WALL AREA (spec §82-15/16). Room-facing wall surface is the room's own
 * perimeter times its wall height. Openings are NOT deducted: that is the
 * painting trade's own convention, it is stated on the sheet, and it is
 * the honest reading of the geometry we hold — there is no room-by-room
 * opening-to-surface map in the model to deduct from.
 */
export function wallAreaX100(room: FinishRoom): number | null {
  if (room.perimeterX100 === null) return null;
  // The one wall-surface formula (spec §89), shared with room-bound scope.
  return grossWallAreaX100(room.perimeterX100, room.wallHeightInches);
}

/**
 * CEILING AREA (spec §82-18). The model carries flat ceilings only, so
 * ceiling area is the floor area; a vaulted ceiling would need geometry
 * the design does not yet hold, and inventing a factor would be a guess.
 */
export const ceilingAreaX100 = (room: FinishRoom): number | null => room.floorAreaX100;

export function surfaceAreaX100(room: FinishRoom, surface: FinishSurface): number | null {
  if (surface === "floor") return room.floorAreaX100;
  if (surface === "ceiling") return ceilingAreaX100(room);
  return wallAreaX100(room);
}

export interface ResolveFinishesInput {
  rooms: readonly FinishRoom[];
  project: readonly FinishProjectChoice[];
  overrides: readonly FinishRoomOverride[];
  /** Surfaces to resolve; defaults to all three. */
  surfaces?: readonly FinishSurface[];
}

export function resolveFinishes(input: ResolveFinishesInput): FinishResolution {
  const surfaces = input.surfaces ?? (["floor", "walls", "ceiling"] as const);
  const projectBySurface = new Map(input.project.map((p) => [p.surface, p]));
  const overrideKey = (roomId: string, surface: FinishSurface) => `${roomId}:${surface}`;
  const overrides = new Map(input.overrides.map((o) => [overrideKey(o.roomId, o.surface), o]));

  const rooms: ResolvedRoomFinish[] = [];
  const coverage: FinishCoverage[] = [];
  const reconciliation: FinishReconciliation[] = [];

  for (const surface of surfaces) {
    const choice = projectBySurface.get(surface);
    const recon: FinishReconciliation = {
      surface,
      eligibleAreaX100: 0,
      defaultAreaX100: 0,
      overrides: [],
      excludedAreaX100: 0,
    };
    const byMaterial = new Map<string, FinishCoverage>();

    for (const room of input.rooms) {
      const eligible = eligibility(room.roomTypeId);
      const area = surfaceAreaX100(room, surface);
      const override = overrides.get(overrideKey(room.roomId, surface));

      /* Precedence, in one place (§82-12). An explicit room decision wins;
       * then the project's own choice; then, only where the project said
       * nothing, the room type's suggestion. */
      let materialKey: string | null = null;
      let source: FinishSource = "none";
      if (override) {
        materialKey = override.excluded ? null : override.materialKey;
        source = "room-override";
      } else if (choice?.enabled && eligible === "eligible") {
        materialKey = choice.materialKey;
        source = "project-default";
      } else if (!choice?.enabled && eligible === "eligible") {
        const suggested = suggestedMaterial(room.roomTypeId, surface);
        if (suggested) {
          materialKey = suggested;
          source = "room-type-suggestion";
        }
      }

      rooms.push({
        roomId: room.roomId,
        roomName: room.roomName,
        surface,
        materialKey,
        source,
        areaX100: area,
        eligibility: eligible,
      });

      if (eligible === "eligible" && area !== null) recon.eligibleAreaX100 += area;
      if (override) {
        if (eligible === "eligible" || override.materialKey !== null) {
          recon.overrides.push({
            roomId: room.roomId,
            roomName: room.roomName,
            materialKey: override.excluded ? null : override.materialKey,
            areaX100: area ?? 0,
          });
        }
        if (override.excluded) recon.excludedAreaX100 += area ?? 0;
      } else if (source === "project-default" && area !== null) {
        recon.defaultAreaX100 += area;
      }

      if (materialKey === null) continue;
      const material = materialByKey(materialKey);
      const existing = byMaterial.get(materialKey) ?? {
        surface,
        materialKey,
        displayName: material?.displayName ?? materialKey,
        areaX100: 0,
        roomIds: [],
        measured: 0,
        unmeasured: 0,
      };
      existing.roomIds.push(room.roomId);
      if (area === null) existing.unmeasured += 1;
      else {
        existing.areaX100 += area;
        existing.measured += 1;
      }
      byMaterial.set(materialKey, existing);
    }

    coverage.push(...[...byMaterial.values()].sort((a, b) => b.areaX100 - a.areaX100 || a.materialKey.localeCompare(b.materialKey)));
    reconciliation.push(recon);
  }

  return { rooms, coverage, reconciliation };
}

/* ------------------------------------------------------------------ *
 * Existing conditions (spec §82-3/4/20/21)
 * ------------------------------------------------------------------ */

export type FinishProvenance = "suggested" | "user";

export interface ExistingFinish {
  roomId: string;
  surface: FinishSurface;
  materialKey: string | null;
  provenance: FinishProvenance;
}

/**
 * MAY THE REMOVAL BE PRICED? (spec §82-4/20/21)
 *
 * A guess about what is on the floor must never spend the owner's money.
 * Only an existing finish somebody CONFIRMED can turn on the tear-out
 * inside a flooring assembly; a room-type suggestion, or nothing at all,
 * leaves it off — the installation is still priced, the demolition is
 * not, and the reason is stated.
 */
export function removalAllowed(existing: ExistingFinish | undefined): {
  allowed: boolean;
  reason: string;
} {
  if (!existing || existing.materialKey === null) {
    return { allowed: false, reason: "Existing floor covering has not been confirmed, so no removal is priced." };
  }
  if (existing.provenance !== "user") {
    return {
      allowed: false,
      reason: `Existing ${existing.materialKey} is a suggestion, not a confirmed condition — no removal is priced until it is confirmed.`,
    };
  }
  return { allowed: true, reason: "Existing floor covering was confirmed, so its removal is included." };
}
